export interface RevenueStatsDataPoint {
	yearMonth: string;
	productType: string | null;
	totalRevenue: number;
	mrr: number;
	addOn: number;
	subscriptionRevenueAcquired: number;
	subscriptionValueChurned: number;
}

export interface RevenueStatsMetrics {
	totalRevenue: number;
	totalMrr: number;
	totalAddOn: number;
	totalSubscriptionRevenueAcquired: number;
	totalSubscriptionValueChurned: number;
}

export interface RevenueStatsResponse {
	data: RevenueStatsDataPoint[];
	metrics: RevenueStatsMetrics;
}

export interface RevenueStatsQuery {
	[key: string]: string | undefined;
	startMonth: string;
	endMonth: string;
	groupBy?: 'all' | 'productType';
	productTypes?: string;
}

export interface RevenueStatsFilters {
	startMonth: string;
	endMonth: string;
	groupBy: 'all' | 'productType';
	productTypes: string[];
}

export interface RevenueMetricToggles {
	totalRevenue: boolean;
	mrr: boolean;
	addOn: boolean;
	subscriptionRevenueAcquired: boolean;
	subscriptionValueChurned: boolean;
}

// ============================================
// Daily Stats Types (for daily_stats_full view)
// ============================================

export interface DailyStatsDataPoint {
	reportingDate: string;
	productType: string | null;
	totalRevenue: number;
	drr: number;
	nrr: number;
	addOn: number;
	subscriptionRevenueAcquired: number;
	subscriptionValueChurned: number;
}

export interface DailyStatsMetrics {
	totalRevenue: number;
	totalDrr: number;
	totalNrr: number;
	totalAddOn: number;
	totalSubscriptionRevenueAcquired: number;
	totalSubscriptionValueChurned: number;
}

export interface DailyStatsResponse {
	data: DailyStatsDataPoint[];
	metrics: DailyStatsMetrics;
}

export interface DailyStatsTablePagination {
	page: number;
	pageSize: number;
	totalPages: number;
	totalRows: number;
}

export interface DailyStatsTableResponse {
	data: DailyStatsDataPoint[];
	pagination: DailyStatsTablePagination;
}

export interface DailyRevenueDashboardQuery {
	[key: string]: string | undefined;
	startDate: string;
	endDate: string;
	groupBy?: 'all' | 'productType';
	productTypes?: string;
	page?: string;
	pageSize?: string;
	includeStats?: string;
}

export interface DailyRevenueDashboardResponse {
	stats?: DailyStatsResponse;
	table: DailyStatsTableResponse;
}

export interface DailyStatsFilters {
	startDate: string;
	endDate: string;
	groupBy: 'all' | 'productType';
	productTypes: string[];
}

export interface DailyMetricToggles {
	totalRevenue: boolean;
	drr: boolean;
	nrr: boolean;
	addOn: boolean;
	subscriptionRevenueAcquired: boolean;
	subscriptionValueChurned: boolean;
}

export enum TimePeriod {
	Daily = 'daily',
	Monthly = 'monthly',
	Weekly = 'weekly',
}

// ============================================
// Subscription Stats Types (for Chart 1 & 2)
// ============================================

export interface DailySubscriptionStatsDataPoint {
	reportingDate: string;
	productType: string | null;
	subscriptionsAcquired: number;
	subscriptionsChurned: number;
	subscribersAcquired: number;
	subscribersChurned: number;
}

export interface DailySubscriptionStatsMetrics {
	netSubscriptions: number;
	totalSubscriptionsAcquired: number;
	totalSubscriptionsChurned: number;
	totalSubscriptionsPaused: number;
	totalSubscribersAcquired: number;
	totalSubscribersChurned: number;
}

export interface DailySubscriptionStatsResponse {
	data: DailySubscriptionStatsDataPoint[];
	metrics: DailySubscriptionStatsMetrics;
}

export interface DailySubscriptionTableRow {
	productType: string;
	subscriptionsAcquired: number;
}

export interface DailySubscriptionTableResponse {
	data: DailySubscriptionTableRow[];
}

export interface DailySubscriptionDashboardQuery {
	[key: string]: string | undefined;
	startDate: string;
	endDate: string;
	groupBy?: 'all' | 'productType';
	productTypes?: string;
}

export interface DailySubscriptionDashboardResponse {
	stats: DailySubscriptionStatsResponse;
	table: DailySubscriptionTableResponse;
}

export interface SubscriptionMetricToggles {
	showAcquired: boolean;
	showChurned: boolean;
}

// ============================================
// Subscription Analysis Types (for Charts 3, 4, 5 & 6)
// ============================================

export interface SubscriptionAnalysisMetric {
	productType: string | null;
	activeSubscriptions: number;
	totalSubscriptionsChurned30d: number;
	startSubscriptions30d: number;
	subscriptionChurnRatePercent30d: number;
}

// Chart 5: Cancellation Reasons (Donut)
export interface CancellationReason {
	cancellationReason: string;
	numberOfCancellations: number;
}

export interface CancellationReasonsData {
	data: CancellationReason[];
	totalCancellations: number;
}

// Chart 6: Subscriber Metrics (Bar + Card)
export interface SubscriberDistribution {
	numberOfSubscriptions: number;
	subscriberCount: number;
}

export interface SubscriberMetricsData {
	distribution: SubscriberDistribution[];
	ratio: number;
	totalSubscribers: number;
}

// ============================================
// Cohort Retention Types (for Cohort Retention Table)
// ============================================

export interface CohortRetentionWeeks {
	w0: number | null;
	w1: number | null;
	w2: number | null;
	w3: number | null;
	w4: number | null;
	w5: number | null;
	w6: number | null;
	w7: number | null;
	w8Plus: number | null;
}

export interface CohortRetentionPivoted {
	cohort: string;
	total: number;
	weeks: CohortRetentionWeeks;
}

export interface CohortRetentionData {
	data: CohortRetentionPivoted[];
	maxWeek: number;
}

// Response with all charts data (Charts 3, 4, 5, 6 & Cohort Retention)
export interface SubscriptionAnalysisResponse {
	data: SubscriptionAnalysisMetric[];
	totals: SubscriptionAnalysisMetric;
	cancellationReasons: CancellationReasonsData;
	subscriberMetrics: SubscriberMetricsData;
	cohortRetention: CohortRetentionData;
}

// ============================================
// Grouped Subscription Analysis Types (Subscription Groups view)
// ============================================

/**
 * Response structure for the grouped subscription analysis endpoint.
 *
 * Mirrors the "Active Subscriptions by Product Type" chart but is sourced from
 * the grouped 30-day rollup view, where `productType` actually holds the
 * subscription group name. Mirrors SubscriptionAnalysisResponse — same shape,
 * different data source. Powers all 5 cards of the Subscription Groups view:
 * - Active subscriptions donut (data + totals)
 * - 30-day metrics card (totals)
 * - Cohort retention heatmap (cohortRetention)
 * - Churn rate chart (data + totals)
 * - Cancellation reasons donut (cancellationReasons)
 * - Subscriptions per subscriber (subscriberMetrics)
 *
 * Note: this is currently only available for the Eby tenant. Other tenants
 * will receive an error from the underlying BigQuery query.
 */
export interface GroupedSubscriptionAnalysisResponse {
	data: SubscriptionAnalysisMetric[];
	totals: SubscriptionAnalysisMetric;
	cancellationReasons: CancellationReasonsData;
	subscriberMetrics: SubscriberMetricsData;
	cohortRetention: CohortRetentionData;
}

// ============================================
// Weekly Add-On Revenue Types (from orders_simplified)
// ============================================

/**
 * Weekly add-on stats aggregated by ISO week
 */
export interface WeeklyAddOnStats {
	weekIdentifier: string; // ISO week format: "2026-W03"
	weekStartDate: string; // Date format: "2026-01-13"
	totalRevenue: number; // Sum of add-on revenue for the week
	totalItems: number; // Count of add-on items sold
}

/**
 * Summary metrics for the entire date range
 */
export interface WeeklyAddOnMetrics {
	totalRevenue: number; // Total add-on revenue for the period
	totalItems: number; // Total add-on items for the period
	addOnPerSubscriber: number; // Revenue per subscriber (totalRevenue / existingSubscribers)
}

/**
 * API response for weekly add-on revenue endpoint
 */
export interface WeeklyAddOnRevenueResponse {
	data: WeeklyAddOnStats[]; // Weekly breakdown data
	metrics: WeeklyAddOnMetrics; // Summary metrics
}

/**
 * Query parameters for weekly add-on revenue endpoint
 */
export interface WeeklyAddOnRevenueQuery {
	[key: string]: string | undefined;
	startDate: string; // Date format: "YYYY-MM-DD"
	endDate: string; // Date format: "YYYY-MM-DD"
}
